/**
 * =============================================================================
 * TABLE SKELETON COMPONENT
 * =============================================================================
 *
 * Skeleton loading states for the question queue table.
 *
 * EXTRACTED FROM: QuestionQueue.tsx (P1-17)
 *
 * B-6 WP-7a (design-v3 §H, CL-13): cells derive from a required `columns`
 * shape spec instead of a hardcoded 4-cell row, so the skeleton can never
 * drift from the real table again. The sole consumer (QuestionQueue) passes
 * shapes derived from its module-level QUEUE_COLUMN_SPEC — the same array
 * that builds its <Table> columns. The pulse animation carries the Q7
 * `prefers-reduced-motion` guard.
 *
 * =============================================================================
 */

import React from 'react';
import { Box, Flex, FlexItem } from '@bigcommerce/big-design';
import { tokens } from '../../tokens';

/**
 * Shape of one skeleton cell: `'flex'` grows to share the remaining row
 * width (a table column with no declared width); `{ width }` renders fixed.
 */
export type TableSkeletonColumn = 'flex' | { width: string };

/**
 * Skeleton loader component for a single row.
 */
function SkeletonRow({
  columns,
}: {
  columns: ReadonlyArray<TableSkeletonColumn>;
}): React.ReactElement {
  return (
    <Box padding="medium">
      <Flex flexGap="1rem" alignItems="center">
        {columns.map((shape, index) => (
          <FlexItem
            key={index}
            flexGrow={shape === 'flex' ? 1 : undefined}
            style={shape === 'flex' ? undefined : { width: shape.width }}
          >
            <Box
              backgroundColor="secondary20"
              data-skeleton-pulse
              style={{
                height: '20px',
                borderRadius: tokens.dimension.radius.sm,
                animation: 'pulse 1.5s ease-in-out infinite',
                animationDelay: index === 0 ? undefined : `${index * 100}ms`,
              }}
            />
          </FlexItem>
        ))}
      </Flex>
    </Box>
  );
}

/**
 * Props for TableSkeleton component.
 */
export interface TableSkeletonProps {
  /**
   * One entry per table column — pass the shapes from the table's column
   * spec so skeleton and table stay in lockstep (design-v3 §H).
   */
  columns: ReadonlyArray<TableSkeletonColumn>;
  /** Optional test ID for testing */
  testId?: string;
  /** Number of skeleton rows to display */
  rowCount?: number;
}

/**
 * Loading skeleton for the entire table.
 *
 * Displays animated skeleton rows to indicate loading state, one cell per
 * table column.
 *
 * @example
 * ```tsx
 * {isLoading && (
 *   <TableSkeleton
 *     testId="queue"
 *     columns={QUEUE_COLUMN_SPEC.map((c) => c.shape)}
 *     rowCount={5}
 *   />
 * )}
 * ```
 */
export function TableSkeleton({
  columns,
  testId,
  rowCount = 5,
}: TableSkeletonProps): React.ReactElement {
  return (
    <Box data-testid={testId ? `${testId}-skeleton` : undefined}>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @media (prefers-reduced-motion: reduce) {
            [data-skeleton-pulse] { animation: none !important; }
          }
        `}
      </style>
      {Array.from({ length: rowCount }, (_, i) => (
        <SkeletonRow key={i} columns={columns} />
      ))}
    </Box>
  );
}
